import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { AlertTriangle, FileText, Loader2, Sparkles } from 'lucide-react'

interface AiCitation {
	source: 'knowledge_doc' | 'previous_reply'
	title?: string
	excerpt?: string
}

interface AiSuggestion {
	draft: string
	confidence?: number
	citations?: AiCitation[]
	notes?: string
}

interface AiSuggestionResponse {
	suggestion: AiSuggestion
	eventId: string | null
}

interface AiAssistantStatus {
	enabled: boolean
	canGenerateNow: boolean
	aiAnswersUsed: number
	aiAnswersLimit: number
	legacyAddon: boolean
	openAIConfigured: boolean
	docsReadyCount: number
	isPremium: boolean
}

interface AiDraftAssistantPanelProps {
	teamId?: string | null
	programId?: string | null
	feedbackType: 'contacts' | 'bugs' | 'features'
	feedbackId?: string | null
	canWrite: boolean
	onApplyDraft: (draft: string, eventId: string | null) => void
	onSendReply: (message: string, eventId: string | null, wasEdited: boolean) => Promise<void> | void
	isSendingReply?: boolean
	onSuggestionCleared?: () => void
	resetToken?: string | number | null
	showKnowledgeBaseLink?: boolean
}

function getNextUtcResetLabel() {
	const now = new Date()
	const nextMonth = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0)
	)
	return nextMonth.toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'UTC',
	})
}

export function AiDraftAssistantPanel({
	teamId,
	programId,
	feedbackType,
	feedbackId,
	canWrite,
	onApplyDraft,
	onSendReply,
	isSendingReply = false,
	onSuggestionCleared,
	resetToken,
	showKnowledgeBaseLink = false,
}: AiDraftAssistantPanelProps) {
	const queryClient = useQueryClient()
	const [isGenerating, setIsGenerating] = useState(false)
	const [aiSuggestion, setAiSuggestion] = useState<AiSuggestion | null>(null)
	const [aiSuggestionEventId, setAiSuggestionEventId] = useState<string | null>(null)
	const [aiSuggestionError, setAiSuggestionError] = useState<string | null>(null)
	const [editedDraft, setEditedDraft] = useState<string>('')

	useEffect(() => {
		setAiSuggestion(null)
		setAiSuggestionEventId(null)
		setAiSuggestionError(null)
		setEditedDraft('')
		onSuggestionCleared?.()
	}, [feedbackId, resetToken])

	const { data: aiAssistantStatus } = useQuery<AiAssistantStatus>({
		queryKey: ['ai-assistant-status', teamId, programId],
		queryFn: async () => {
			if (!teamId) {
				throw new Error('Team ID is required')
			}

			const params = new URLSearchParams({ teamId })
			if (programId) params.append('programId', programId)

			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/status?${params.toString()}`,
				{
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				throw new Error('Failed to fetch AI assistant status')
			}

			return response.json()
		},
		enabled: Boolean(teamId),
		staleTime: 30000,
	})

	const quotaLabel = useMemo(() => {
		if (!aiAssistantStatus || aiAssistantStatus.legacyAddon) return null
		if (aiAssistantStatus.aiAnswersLimit <= 0) return null
		return `${aiAssistantStatus.aiAnswersUsed}/${aiAssistantStatus.aiAnswersLimit} AI answers used this month`
	}, [aiAssistantStatus])

	const generateSuggestion = async () => {
		if (!feedbackId) return

		setIsGenerating(true)
		setAiSuggestionError(null)

		try {
			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/${feedbackType}/${feedbackId}/suggest-reply`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...await getAuthHeaders(),
					},
					body: JSON.stringify({}),
				}
			)

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}))
				if (response.status === 402 || errorBody?.code === 'ai_limit_reached') {
					await queryClient.invalidateQueries({
						queryKey: ['ai-assistant-status', teamId, programId],
					})
					setAiSuggestionError(
						`AI answer quota reached. Resets ${getNextUtcResetLabel()}.`
					)
					return
				}
				throw new Error(errorBody?.error || 'Failed to generate AI suggestion')
			}

			const data: AiSuggestionResponse = await response.json()
			setAiSuggestion(data.suggestion)
			setAiSuggestionEventId(data.eventId)
			setEditedDraft(data.suggestion.draft)
			await queryClient.invalidateQueries({
				queryKey: ['ai-assistant-status', teamId, programId],
			})
		} catch (error) {
			setAiSuggestionError(
				error instanceof Error ? error.message : 'Failed to generate AI suggestion'
			)
		} finally {
			setIsGenerating(false)
		}
	}

	const canGenerate =
		canWrite &&
		Boolean(aiAssistantStatus?.enabled) &&
		Boolean(aiAssistantStatus?.canGenerateNow) &&
		Boolean(aiAssistantStatus?.openAIConfigured)

	const exhausted =
		Boolean(aiAssistantStatus?.enabled) &&
		!aiAssistantStatus?.legacyAddon &&
		aiAssistantStatus?.aiAnswersLimit > 0 &&
		(aiAssistantStatus?.aiAnswersUsed || 0) >= aiAssistantStatus.aiAnswersLimit

	return (
		<div className="rounded-xl border bg-gradient-to-br from-primary/5 via-background to-background p-4 space-y-3">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
							<Sparkles className="h-4 w-4" />
						</div>
						<p className="text-sm font-semibold">AI Reply Assistant</p>
					</div>
					<p className="text-xs text-muted-foreground">
						Generate a ready-to-send draft from past replies and your knowledge base.
					</p>
					{quotaLabel && (
						<p className="text-xs text-muted-foreground">{quotaLabel}</p>
					)}
					{showKnowledgeBaseLink && (
						<div className="flex items-center gap-2 text-xs text-muted-foreground">
							<FileText className="h-3.5 w-3.5" />
							<a href="/knowledge-base" className="underline underline-offset-2">
								Manage knowledge base
							</a>
						</div>
					)}
				</div>
				<Button
					type="button"
					variant={canGenerate ? 'default' : 'secondary'}
					size="sm"
					className="sm:self-center"
					onClick={generateSuggestion}
					disabled={!canGenerate || isGenerating}
				>
					{isGenerating ? (
						<Loader2 className="h-4 w-4 mr-2 animate-spin" />
					) : (
						<Sparkles className="h-4 w-4 mr-2" />
					)}
					Generate Draft
				</Button>
			</div>

			{!aiAssistantStatus?.enabled && (
				<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
					Upgrade to Growth for 200 AI answers per month.{' '}
					<a
						href="/settings?tab=billing"
						className="underline font-medium underline-offset-2"
					>
						Open Billing
					</a>
				</div>
			)}

			{aiAssistantStatus?.enabled && !aiAssistantStatus?.openAIConfigured && (
				<div className="rounded-lg border border-muted bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-start gap-2">
					<AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
					<p>OpenAI is not configured for this environment.</p>
				</div>
			)}

			{exhausted && (
				<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
					You&apos;ve used all {aiAssistantStatus?.aiAnswersLimit} AI answers this
					month. Resets {getNextUtcResetLabel()}.
				</div>
			)}

			{aiSuggestionError && (
				<div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2">
					<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
					<p>{aiSuggestionError}</p>
				</div>
			)}

			{aiSuggestion && (
				<Card className="rounded-xl border border-primary/20 bg-background p-4 space-y-3 shadow-sm">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm font-semibold">Suggested Draft</p>
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={generateSuggestion}
								disabled={!canGenerate || isGenerating}
							>
								{isGenerating && (
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								)}
								Regenerate (uses 1 AI answer)
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => {
									setAiSuggestion(null)
									setAiSuggestionEventId(null)
									setEditedDraft('')
									onSuggestionCleared?.()
								}}
							>
								Discard
							</Button>
						</div>
					</div>
					<textarea
						className="w-full rounded-lg border bg-muted/30 p-3 text-sm leading-6 resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
						value={editedDraft}
						onChange={(e) => setEditedDraft(e.target.value)}
						disabled={isSendingReply}
					/>
					<div className="flex justify-end">
						<Button
							type="button"
							variant="default"
							size="sm"
							onClick={async () => {
								try {
									const wasEdited = editedDraft.trim() !== aiSuggestion.draft.trim()
									await onSendReply(editedDraft, aiSuggestionEventId, wasEdited)
									setAiSuggestion(null)
									setAiSuggestionEventId(null)
									setEditedDraft('')
									onSuggestionCleared?.()
								} catch {
									// Keep draft visible on error
								}
							}}
							disabled={!editedDraft.trim() || isSendingReply}
						>
							{isSendingReply ? (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							) : null}
							{isSendingReply ? 'Sending...' : 'Send Reply'}
						</Button>
					</div>
					{(aiSuggestion.citations || []).length > 0 && (
						<div className="space-y-2 border-t pt-3">
							<p className="text-xs font-medium text-muted-foreground">Sources</p>
							<div className="space-y-2">
								{(aiSuggestion.citations || []).map((citation, idx) => (
									<div
										key={`${citation.title || 'source'}-${idx}`}
										className="rounded-md border bg-muted/20 px-3 py-2 text-xs"
									>
										<p className="font-medium">
											{citation.title ||
												(citation.source === 'knowledge_doc'
													? 'Knowledge Doc'
													: 'Previous Reply')}
										</p>
										{citation.excerpt && (
											<p className="text-muted-foreground mt-1.5">
												{citation.excerpt}
											</p>
										)}
									</div>
								))}
							</div>
						</div>
					)}
				</Card>
			)}
		</div>
	)
}
